import { useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CartState } from '@/hooks/useCart';
import SkuComponentGroups from '@/components/feature/SkuComponentGroups';

export interface MiniCartProps {
  cart: CartState;
  isOpen: boolean;
  onClose: () => void;
  onRemove: (id: string) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  variant?: 'popover' | 'sheet';
}

export default function MiniCart({
  cart,
  isOpen,
  onClose,
  onRemove,
  onUpdateQuantity,
  variant = 'popover',
}: MiniCartProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const totalOld = cart.items.reduce((sum, item) => {
    const unitOld = item.oldPrice ?? item.price;
    return sum + unitOld * item.quantity;
  }, 0);
  const discount = totalOld - cart.total;
  const isSheet = variant === 'sheet';

  return (
    <div className={isSheet ? 'fixed inset-0 z-[70] bg-dark/25' : 'fixed inset-0 z-[60] pointer-events-none'}>
      <div
        ref={panelRef}
        className={
          isSheet
            ? 'absolute bottom-0 left-0 right-0 mx-auto max-h-[82vh] w-full max-w-md overflow-hidden rounded-t-3xl border border-sand/60 bg-card shadow-hover'
            : 'absolute top-16 right-4 w-[calc(100vw-2rem)] max-w-[400px] overflow-hidden rounded-3xl border border-sand/60 bg-card shadow-hover pointer-events-auto md:top-20 md:right-8'
        }
      >
        <div className="px-5 py-4 border-b border-sand/60 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-dark">
            {cart.count === 0 ? 'Корзина пуста' : `Товар${cart.count > 1 ? 'ов' : ''} в корзине`}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-dark/5 transition-colors"
            aria-label="Закрыть"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-close-line text-dark/60" />
            </div>
          </button>
        </div>

        <div className={isSheet ? 'max-h-[48vh] overflow-y-auto' : 'max-h-[320px] overflow-y-auto'}>
          {cart.items.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-sand/60">
                <i className="ri-shopping-bag-3-line text-2xl text-honey" />
              </div>
              <p className="text-sm text-dark/50">Добавьте товары из каталога</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {cart.items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-3 p-2 rounded-2xl hover:bg-sand/40 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`/product/${item.productId || item.id}`);
                    }}
                    className="w-16 h-16 rounded-2xl overflow-hidden bg-sand shrink-0 cursor-pointer"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`/product/${item.productId || item.id}`);
                      }}
                      className="text-sm font-medium text-dark truncate text-left w-full hover:text-honey-dark transition-colors cursor-pointer"
                    >
                      {item.name}
                    </button>
                    <p className="text-xs text-dark/50 mt-0.5">{item.size}</p>
                    <SkuComponentGroups components={item.components} itemQuantity={item.quantity} />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-sand/60 text-dark/60 hover:bg-honey/20 hover:text-honey-dark transition-colors text-xs"
                        >
                          −
                        </button>
                        <span className="text-sm font-medium w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-sand/60 text-dark/60 hover:bg-honey/20 hover:text-honey-dark transition-colors text-xs"
                        >
                          +
                        </button>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-dark">
                          {(item.price * item.quantity).toLocaleString('ru-RU')} ₽
                        </span>
                        {item.oldPrice && item.oldPrice > item.price && (
                          <span className="block text-xs text-dark/30 line-through">
                            {(item.oldPrice * item.quantity).toLocaleString('ru-RU')} ₽
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onRemove(item.id)}
                    className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-dark/30 hover:text-red-500 transition-colors self-start"
                    aria-label="Удалить"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-sm" />
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.items.length > 0 && (
          <div className="telegram-sheet-actions space-y-2 border-t border-sand/60 px-5 py-4">
            {discount > 0 && (
              <>
                <div className="flex items-center justify-between text-sm text-dark/60">
                  <span className="line-through">Сумма без скидки</span>
                  <span className="line-through">{totalOld.toLocaleString('ru-RU')} ₽</span>
                </div>
                <div className="flex items-center justify-between text-sm text-red-500">
                  <span>Скидка</span>
                  <span>−{discount.toLocaleString('ru-RU')} ₽</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-dark/60">Итого</span>
              <span className="text-lg font-bold text-dark">
                {cart.total.toLocaleString('ru-RU')} ₽
              </span>
            </div>
            <div className="flex gap-2 pt-2">
              <Link
                to="/cart"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium bg-sand text-dark hover:bg-honey/20 transition-colors text-center whitespace-nowrap"
              >
                В корзину
              </Link>
              <Link
                to="/checkout"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium bg-honey text-white hover:bg-honey-dark transition-colors text-center whitespace-nowrap"
              >
                Оформить
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
